import { create } from 'zustand';
import {
  SalonInvoiceProfile,
  DEFAULT_SALON_PROFILE,
  fetchSalonInvoiceProfile,
  saveSalonInvoiceProfile,
} from '../services/peInvoiceFirebaseService';
import { getPeInvoiceVatFromAppStore } from '../utils/pe-papers/peInvoiceVatFromAppStore';
import {
  getBarometerHourlyRateFromAppStore,
  mergePeInvoiceProfileWithStammdaten,
} from '../utils/pe-papers/peInvoiceSenderFromAppStore';
export type CustomerType = 'private' | 'business';
export type InvoiceItemKind = 'service' | 'sale';
export type MaterialMode = 'inclusive' | 'exclusive';

export interface InvoiceCustomer {
  type: CustomerType;
  // Privatkunde
  firstName: string;
  lastName: string;
  // Firma / Geschäftskunde
  companyName: string;
  contactPerson: string;
  vatId: string;
  buyerReference: string;
  email: string;
  // Gemeinsame Adressdaten
  street: string;
  zip: string;
  city: string;
  country: string;
}

export interface InvoiceItem {
  id: string;
  kind: InvoiceItemKind; // Dienstleistung | Verkauf
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  priceMode: 'gross' | 'net';
  vatRate: number;
  // Nur relevant bei kind === 'service'
  serviceHours: number;
  hourlyRate: number;
  materialMode: MaterialMode;
}

export interface InvoiceShipping {
  enabled: boolean;
  amount: number;
  vatRate: number;
}

export type InvoiceDocType = 'invoice' | 'estimate';

export interface InvoiceMeta {
  docType: InvoiceDocType; // 'invoice' = Rechnung, 'estimate' = Kostenvoranschlag
  invoiceNumber: string;
  invoiceDate: string; // YYYY-MM-DD
  deliveryDate: string; // YYYY-MM-DD (bei KV: Gültig bis)
  paymentDeadlineDays: number; // Zahlungsziel bzw. Bindefrist in Tagen
  notes: string;
}

export interface InvoiceCalculations {
  netTotal: number;
  grossTotal: number;
  vatTotal: number;
  standardVatRate: number;
  isVatExempt: boolean;
  vatBreakdown: {
    standard: { rate: number; net: number; vat: number };
  };
}

export interface PEInvoiceState {
  profile: SalonInvoiceProfile;
  customer: InvoiceCustomer;
  items: InvoiceItem[];
  shipping: InvoiceShipping;
  meta: InvoiceMeta;
  isLoading: boolean;
  isSaving: boolean;

  // Actions Profile
  loadProfile: (mandantId: string) => Promise<void>;
  updateProfile: (mandantId: string, updates: Partial<SalonInvoiceProfile>) => Promise<void>;

  // Actions Customer
  setCustomerType: (type: CustomerType) => void;
  updateCustomer: (updates: Partial<InvoiceCustomer>) => void;

  // Actions Items
  addItem: () => void;
  updateItem: (id: string, updates: Partial<InvoiceItem>) => void;
  removeItem: (id: string) => void;
  /** Stundensätze aus Kalkulator-Barometer (Soll) in alle DL-Positionen schreiben */
  syncBarometerRates: () => void;

  // Actions Shipping & Meta
  updateShipping: (updates: Partial<InvoiceShipping>) => void;
  updateMeta: (updates: Partial<InvoiceMeta>) => void;
  setDocType: (docType: InvoiceDocType) => void;

  // Calculations Helper
  getCalculations: () => InvoiceCalculations;
  resetInvoice: () => void;
  /** Entwurf / Archiv-Dokument in den Editor laden */
  loadInvoiceDraft: (draft: {
    customer?: Partial<InvoiceCustomer>;
    items?: InvoiceItem[];
    shipping?: Partial<InvoiceShipping>;
    meta?: Partial<InvoiceMeta>;
  }) => void;
}

const getTodayIso = () => new Date().toISOString().split('T')[0];

const defaultVatRate = () => getPeInvoiceVatFromAppStore().standardRate;

const applyBarometerToServiceItem = (item: InvoiceItem): InvoiceItem => {
  if ((item.kind || 'service') !== 'service') return item;
  const materialMode = item.materialMode || 'exclusive';
  const barometerRate = getBarometerHourlyRateFromAppStore(materialMode);
  if (!(barometerRate > 0)) return item;
  const serviceHours = item.serviceHours ?? 0;
  return {
    ...item,
    kind: 'service',
    materialMode,
    hourlyRate: barometerRate,
    unitPrice: barometerRate,
    unit: 'Std.',
    quantity: serviceHours > 0 ? serviceHours : item.quantity || 1,
  };
};

const createBlankItem = (id: string): InvoiceItem => {
  const barometerRate = getBarometerHourlyRateFromAppStore('exclusive');
  return {
    id,
    kind: 'service',
    description: '',
    quantity: 1,
    unit: 'Std.',
    unitPrice: barometerRate || 0,
    priceMode: 'gross',
    vatRate: defaultVatRate(),
    serviceHours: 0,
    hourlyRate: barometerRate || 0,
    materialMode: 'exclusive',
  };
};

export const usePEInvoiceStore = create<PEInvoiceState>((set, get) => ({
  profile: DEFAULT_SALON_PROFILE,
  customer: {
    type: 'private',
    firstName: '',
    lastName: '',
    companyName: '',
    contactPerson: '',
    vatId: '',
    buyerReference: '',
    email: '',
    street: '',
    zip: '',
    city: '',
    country: 'DE',
  },
  items: [createBlankItem('item-1')],
  shipping: {
    enabled: false,
    amount: 0,
    vatRate: defaultVatRate(),
  },
  meta: {
    docType: 'invoice',
    invoiceNumber: '',
    invoiceDate: getTodayIso(),
    deliveryDate: getTodayIso(),
    paymentDeadlineDays: 14,
    notes: '',
  },
  isLoading: false,
  isSaving: false,

  loadProfile: async (mandantId: string) => {
    set({ isLoading: true });
    const profile = await fetchSalonInvoiceProfile(mandantId);
    const { standardRate, isVatExempt } = getPeInvoiceVatFromAppStore();
    const mergedProfile = mergePeInvoiceProfileWithStammdaten(profile);

    set((state) => ({
      profile: mergedProfile,
      isLoading: false,
      meta: {
        ...state.meta,
        paymentDeadlineDays: mergedProfile.defaultPaymentTermsDays || 14,
        invoiceNumber:
          state.meta.invoiceNumber ||
          `${mergedProfile.invoicePrefix}${Date.now().toString().slice(-4)}`,
      },
      items: state.items.map((item) => {
        const kind = item.kind || 'service';
        const materialMode = item.materialMode || 'exclusive';
        const synced =
          kind === 'service'
            ? applyBarometerToServiceItem({ ...item, kind, materialMode })
            : item;
        return {
          ...synced,
          vatRate: isVatExempt ? 0 : standardRate,
          unit: kind === 'service' ? synced.unit || 'Std.' : synced.unit || 'Stk.',
        };
      }),
      shipping: {
        ...state.shipping,
        vatRate: isVatExempt ? 0 : standardRate,
      },
    }));
  },

  updateProfile: async (mandantId: string, updates: Partial<SalonInvoiceProfile>) => {
    set({ isSaving: true });
    const newProfile = { ...get().profile, ...updates };
    set({ profile: newProfile });
    await saveSalonInvoiceProfile(mandantId, updates);
    set({ isSaving: false });
  },

  setCustomerType: (type: CustomerType) =>
    set((state) => ({ customer: { ...state.customer, type } })),

  updateCustomer: (updates: Partial<InvoiceCustomer>) =>
    set((state) => ({ customer: { ...state.customer, ...updates } })),

  addItem: () =>
    set((state) => ({
      items: [...state.items, createBlankItem(`item-${Date.now()}`)],
    })),

  syncBarometerRates: () =>
    set((state) => ({
      items: state.items.map((item) =>
        (item.kind || 'service') === 'service' ? applyBarometerToServiceItem(item) : item
      ),
    })),

  updateItem: (id: string, updates: Partial<InvoiceItem>) =>
    set((state) => ({
      items: state.items.map((item) => {
        if (item.id !== id) return item;
        const next = { ...item, ...updates };
        const manualRateEdit = updates.hourlyRate !== undefined;

        if (updates.kind === 'sale') {
          if (next.unit === 'Std.') next.unit = 'Stk.';
          return next;
        }

        if ((next.kind || 'service') !== 'service') return next;

        next.kind = 'service';
        next.unit = 'Std.';

        // Material-Umschalter → Barometer-Satz für inkl./exkl.
        if (updates.materialMode !== undefined) {
          const rate = getBarometerHourlyRateFromAppStore(updates.materialMode);
          if (rate > 0) {
            next.hourlyRate = rate;
            next.unitPrice = rate;
          }
        } else if (updates.serviceHours !== undefined || updates.kind === 'service') {
          // Zeitaufwand / Wechsel auf DL → immer frischer Barometer-Soll
          const synced = applyBarometerToServiceItem(next);
          next.hourlyRate = synced.hourlyRate;
          next.unitPrice = synced.unitPrice;
          next.quantity = synced.quantity;
        } else if (!manualRateEdit && !(next.hourlyRate > 0)) {
          const synced = applyBarometerToServiceItem(next);
          next.hourlyRate = synced.hourlyRate;
          next.unitPrice = synced.unitPrice;
        }

        if (manualRateEdit) {
          next.unitPrice = Number(updates.hourlyRate) || 0;
        }

        const hours = next.serviceHours ?? 0;
        if (hours > 0) {
          next.quantity = hours;
          next.unit = 'Std.';
          if (next.hourlyRate > 0) next.unitPrice = next.hourlyRate;
        }

        return next;
      }),
    })),

  removeItem: (id: string) =>
    set((state) => ({
      items: state.items.filter((item) => item.id !== id),
    })),

  updateShipping: (updates: Partial<InvoiceShipping>) =>
    set((state) => ({ shipping: { ...state.shipping, ...updates } })),

  updateMeta: (updates: Partial<InvoiceMeta>) =>
    set((state) => ({ meta: { ...state.meta, ...updates } })),

  setDocType: (docType: InvoiceDocType) =>
    set((state) => {
      const invoicePrefix = state.profile.invoicePrefix || 'RE-';
      const newPrefix = docType === 'estimate' ? 'KV-' : invoicePrefix;
      const current = (state.meta.invoiceNumber || '').trim();
      let invoiceNumber = state.meta.invoiceNumber;

      const isEmpty = !current;
      const startsWithKv = current.startsWith('KV-');
      const startsWithProfile = current.startsWith(invoicePrefix);

      if (isEmpty || startsWithKv || startsWithProfile) {
        let suffix = '';
        if (startsWithKv) suffix = current.slice('KV-'.length);
        else if (startsWithProfile) suffix = current.slice(invoicePrefix.length);
        if (!suffix) suffix = Date.now().toString().slice(-4);
        invoiceNumber = `${newPrefix}${suffix}`;
      }

      return {
        meta: {
          ...state.meta,
          docType,
          invoiceNumber,
        },
      };
    }),

  getCalculations: () => {
    const { items, shipping } = get();
    const { isVatExempt, standardRate } = getPeInvoiceVatFromAppStore();

    let netStandard = 0;
    let vatStandard = 0;
    let net0 = 0;

    items.forEach((item) => {
      const lineTotal = (item.quantity || 0) * (item.unitPrice || 0);
      const effectiveRate = isVatExempt ? 0 : standardRate;

      if (effectiveRate === 0) {
        net0 += lineTotal;
      } else if (item.priceMode === 'gross') {
        const net = lineTotal / (1 + effectiveRate / 100);
        netStandard += net;
        vatStandard += lineTotal - net;
      } else {
        netStandard += lineTotal;
        vatStandard += lineTotal * (effectiveRate / 100);
      }
    });

    if (shipping.enabled && shipping.amount > 0) {
      const shipRate = isVatExempt ? 0 : standardRate;
      if (shipRate === 0) {
        net0 += shipping.amount;
      } else {
        // Versand als Netto-Betrag (wie bisher)
        netStandard += shipping.amount;
        vatStandard += shipping.amount * (shipRate / 100);
      }
    }

    const netTotal = net0 + netStandard;
    const vatTotal = isVatExempt ? 0 : vatStandard;
    const grossTotal = netTotal + vatTotal;

    return {
      netTotal,
      grossTotal,
      vatTotal,
      standardVatRate: standardRate,
      isVatExempt,
      vatBreakdown: {
        standard: { rate: standardRate, net: netStandard, vat: vatTotal },
      },
    };
  },

  resetInvoice: () =>
    set((state) => {
      const { standardRate } = getPeInvoiceVatFromAppStore();
      return {
        customer: {
          type: 'private',
          firstName: '',
          lastName: '',
          companyName: '',
          contactPerson: '',
          vatId: '',
          buyerReference: '',
          email: '',
          street: '',
          zip: '',
          city: '',
          country: 'DE',
        },
        items: [createBlankItem(`item-${Date.now()}`)],
        shipping: { enabled: false, amount: 0, vatRate: standardRate },
        meta: {
          docType: 'invoice',
          invoiceNumber: `${state.profile.invoicePrefix}${Date.now().toString().slice(-4)}`,
          invoiceDate: getTodayIso(),
          deliveryDate: getTodayIso(),
          paymentDeadlineDays: state.profile.defaultPaymentTermsDays || 14,
          notes: '',
        },
      };
    }),

  loadInvoiceDraft: (draft) =>
    set((state) => {
      const nextCustomer = draft.customer
        ? { ...state.customer, ...draft.customer }
        : state.customer;
      const nextItems =
        Array.isArray(draft.items) && draft.items.length > 0
          ? draft.items.map((item) => ({
              ...createBlankItem(item.id || `item-${Date.now()}`),
              ...item,
            }))
          : state.items;
      const nextShipping = draft.shipping
        ? { ...state.shipping, ...draft.shipping }
        : state.shipping;
      const nextMeta = draft.meta ? { ...state.meta, ...draft.meta } : state.meta;
      return {
        customer: nextCustomer,
        items: nextItems,
        shipping: nextShipping,
        meta: nextMeta,
      };
    }),
}));
